import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { fetchProduct, fetchProducts, fetchBrands } from '../lib/api';
import { useCart } from '../context/CartContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import ProductCard from '../components/ProductCard';
import { Minus, Plus, ShoppingCart, Truck, ShieldCheck, RotateCcw, ChevronRight, Loader2 } from 'lucide-react';

const ProductDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addItem, setIsOpen } = useCart();
  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [brand, setBrand] = useState(null);
  const [qty, setQty] = useState(1);
  const [flavor, setFlavor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setLoading(true);
    setNotFound(false);
    fetchProduct(id)
      .then(async (p) => {
        setProduct(p);
        setFlavor(p.flavors?.[0] || null);
        const [rel, brands] = await Promise.all([
          fetchProducts({ category: p.category }),
          fetchBrands(),
        ]);
        setRelated(rel.filter(r => r.id !== p.id).slice(0, 4));
        setBrand(brands.find(b => b.id === p.brand) || null);
      })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return <div className="flex justify-center py-32"><Loader2 className="w-8 h-8 animate-spin text-neutral-400" /></div>;
  }

  if (notFound || !product) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-20 text-center">
        <h1 className="text-2xl font-bold">Product not found</h1>
        <Button onClick={() => navigate('/shop')} className="mt-4 bg-[#0a0a0a]">Back to Shop</Button>
      </div>
    );
  }

  const handleAdd = () => {
    addItem(product, qty, flavor);
    setIsOpen(true);
  };

  const hasSale = product.originalPrice && product.originalPrice > product.price;

  return (
    <div className="max-w-7xl mx-auto px-4 py-6">
      <nav className="flex items-center text-xs text-neutral-500 mb-6 flex-wrap gap-1">
        <Link to="/" className="hover:text-black">Home</Link>
        <ChevronRight className="w-3 h-3" />
        <Link to="/shop" className="hover:text-black">Shop</Link>
        <ChevronRight className="w-3 h-3" />
        {brand && <><Link to={`/shop?brand=${brand.id}`} className="hover:text-black">{brand.name}</Link><ChevronRight className="w-3 h-3" /></>}
        <span className="text-neutral-800 font-medium line-clamp-1">{product.name}</span>
      </nav>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
        <div className="bg-neutral-50 rounded-lg p-6 md:p-10 relative">
          {!product.inStock && <Badge className="absolute top-4 left-4 z-10 bg-neutral-900">Out of Stock</Badge>}
          {hasSale && <Badge className="absolute top-4 right-4 z-10 bg-[#bfff00] text-black font-bold hover:bg-[#bfff00]">SALE</Badge>}
          <img src={product.image} alt={product.name} className="w-full h-auto max-h-[500px] object-contain" />
        </div>

        <div>
          {brand && <Link to={`/shop?brand=${brand.id}`} className="text-xs font-bold uppercase tracking-widest text-neutral-500 hover:text-black">{brand.name}</Link>}
          <h1 className="text-2xl md:text-3xl font-black mt-2 leading-tight">{product.name}</h1>

          <div className="mt-5 flex items-baseline gap-3">
            <span className="text-3xl font-black text-[#0a0a0a]">${product.price.toFixed(2)}</span>
            {hasSale && <span className="text-lg text-neutral-400 line-through">${product.originalPrice.toFixed(2)}</span>}
            {hasSale && <Badge className="bg-red-50 text-red-700 border border-red-200 hover:bg-red-50">Save ${(product.originalPrice - product.price).toFixed(2)}</Badge>}
          </div>

          <p className="text-neutral-600 leading-relaxed mt-5">{product.description}</p>

          {product.flavors && product.flavors.length > 0 && (
            <div className="mt-6">
              <p className="text-sm font-semibold mb-2">Flavour: <span className="font-normal text-neutral-600">{flavor}</span></p>
              <div className="flex flex-wrap gap-2">
                {product.flavors.map(f => (
                  <button key={f} onClick={() => setFlavor(f)} className={`px-4 py-2 text-sm rounded-md border-2 transition-colors ${flavor === f ? 'border-[#0a0a0a] bg-[#0a0a0a] text-white' : 'border-neutral-300 hover:border-[#0a0a0a]'}`}>{f}</button>
                ))}
              </div>
            </div>
          )}

          <div className="mt-6 flex items-center gap-4">
            <div className="flex items-center border-2 border-neutral-300 rounded-md">
              <button onClick={() => setQty(q => Math.max(1, q - 1))} className="px-3 py-2.5 hover:bg-neutral-100"><Minus className="w-4 h-4" /></button>
              <span className="px-5 font-bold">{qty}</span>
              <button onClick={() => setQty(q => q + 1)} className="px-3 py-2.5 hover:bg-neutral-100"><Plus className="w-4 h-4" /></button>
            </div>
            <Button onClick={handleAdd} disabled={!product.inStock} size="lg" className="flex-1 bg-[#0a0a0a] hover:bg-neutral-800 text-white font-bold uppercase tracking-wide disabled:bg-neutral-300">
              <ShoppingCart className="w-4 h-4 mr-2" /> {product.inStock ? 'Add to Cart' : 'Out of Stock'}
            </Button>
          </div>

          <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs">
            <div className="flex items-center gap-2 p-3 bg-neutral-50 rounded"><Truck className="w-4 h-4 text-[#0a0a0a]" /> Free shipping over $90</div>
            <div className="flex items-center gap-2 p-3 bg-neutral-50 rounded"><ShieldCheck className="w-4 h-4 text-[#0a0a0a]" /> 100% authentic</div>
            <div className="flex items-center gap-2 p-3 bg-neutral-50 rounded"><RotateCcw className="w-4 h-4 text-[#0a0a0a]" /> Easy returns</div>
          </div>
        </div>
      </div>

      {related.length > 0 && (
        <section className="mt-20">
          <h2 className="text-2xl font-black mb-6">You May Also Like</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
            {related.map(p => <ProductCard key={p.id} product={p} />)}
          </div>
        </section>
      )}
    </div>
  );
};

export default ProductDetailPage;
